import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect, useRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import ArrowLeftIcon from "../../resources/icons/arrow-left.svg";
import ArrowRightIcon from "../../resources/icons/arrow-right.svg";
import ZoomInIcon from "../../resources/icons/zoom-in.svg";
import ZoomOutIcon from "../../resources/icons/zoom-out.svg";
import DownloadIcon from "../../resources/icons/download-01.svg";
import Toast from "../Toast/Toast";
import { isMobile } from "react-device-detect";
import CfLoader from "../CfLoader/CfLoader";

export default function EvidenceModal({ isOpen, toggleModal, marksheets }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPercentage, setShowPercentage] = useState(false);
  const [scale, setScale] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const iframeRef = useRef(null);
  const embedRef = useRef(null);

  const documents = marksheets
    ? marksheets
        .filter((item) => item.evidence_url)
        .map((item) => ({
          url: item.evidence_url,
          name: item.name || "Document",
        }))
    : [];

  function closeModal() {
    toggleModal(false);
    setCurrentIndex(0);
    setScale(1);
    setIsLoading(false);
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? documents.length - 1 : prev - 1));
    setScale(1);
    setIsLoading(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === documents.length - 1 ? 0 : prev + 1));
    setScale(1);
    setIsLoading(true);
  };

  useEffect(() => {
    let timer;
    if (showPercentage) {
      timer = setTimeout(() => {
        setShowPercentage(false);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [showPercentage]);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
  }, [isOpen, currentIndex]);

  useEffect(() => {
    if (embedRef.current && !isMobile) {
      const embed = embedRef.current;
      const handleLoad = () => {
        setIsLoading(false);
      };
      
      embed.addEventListener('load', handleLoad);
      
      return () => {
        embed.removeEventListener('load', handleLoad);
      };
    }
    }, [currentIndex, isMobile]);

  const handleframeLoad = () => {
    setIsLoading(false);
  };

  const handleDownload = async () => {
    const currentDoc = documents[currentIndex];
    if (!currentDoc?.url) {
      Toast({
        type: "error",
        title: "Error",
        message: "Download URL not available",
      });
      return;
    }

    try {
      const response = await fetch(currentDoc.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = currentDoc.name || "document";
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to download document",
      });

      const fallbackLink = document.createElement("a");
      fallbackLink.href = currentDoc.url;
      fallbackLink.target = "_blank";
      fallbackLink.rel = "noopener noreferrer";
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
    }
  };

  const renderPdfViewer = (url) => {
    if (isMobile) {
      return (
        <div className="w-full h-full relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <CfLoader />
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={`https://docs.google.com/gview?url=${encodeURIComponent(
              url
            )}&embedded=true`}
            className="w-full h-full border-none"
            title="PDF Viewer"
            onLoad={handleframeLoad}
            style={{ visibility: isLoading ? "hidden" : "visible" }}
          />
        </div>
      );
    } else {
      return (
        <div className="w-full h-full relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <CfLoader />
            </div>
          )}
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={3}
            wheel={{ step: 0.1 }}
            onZoom={(ref) => {
              setScale(ref.state.scale);
              setShowPercentage(true);
            }}
            doubleClick={{ disabled: true }}
            panning={{ velocityDisabled: true }}
          >
            {({ zoomIn, zoomOut }) => (
              <>
                <TransformComponent
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  contentStyle={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <embed
                    ref={embedRef}
                    src={`${url}#toolbar=0&navpanes=0`}
                    type="application/pdf"
                    className="w-full h-full"
                    style={{
                      maxHeight: "100%",
                      maxWidth: "100%",
                    }}
                  />
                </TransformComponent>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-4 bg-cfSecondary rounded-full p-2 shadow-lg">
                  <button
                    onClick={() => {
                      zoomIn();
                      setShowPercentage(true);
                    }}
                    className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                    title="Zoom In"
                  >
                    <img src={ZoomInIcon} alt="Zoom In" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      zoomOut();
                      setShowPercentage(true);
                    }}
                    className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                    title="Zoom Out"
                  >
                    <img src={ZoomOutIcon} alt="Zoom Out" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                    title="Download"
                  >
                    <img
                      src={DownloadIcon}
                      alt="Download"
                      className="w-5 h-5"
                    />
                  </button>
                </div>
              </>
            )}
          </TransformWrapper>
        </div>
      );
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative">
                <button
                  type="button"
                  className="absolute text-xl top-2 right-3 text-cfGlobalText focus:outline-none"
                  onClick={closeModal}
                >
                  <span className="sr-only">Close</span>
                  &#10005;
                </button>

                {documents.length > 0 && (
                  <Dialog.Title className="text-xl font-medium text-cfHeadingText text-center mb-4">
                    {documents[currentIndex].name}
                  </Dialog.Title>
                )}

                <div className="mt-2 relative">
                  <div className="flex justify-center items-center h-[70vh] bg-white">
                    {documents.length > 0 ? (
                      documents[currentIndex].url &&
                      renderPdfViewer(documents[currentIndex].url)
                    ) : (
                      <div className="text-cfGlobalText">
                        No evidence available
                      </div>
                    )}
                  </div>

                  {showPercentage && (
                    <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/50 text-cfSecondary px-3 py-1 rounded-md text-sm transition-opacity duration-300">
                      {Math.round(scale * 100)}%
                    </div>
                  )}

                  {documents.length > 1 && (
                    <>
                      <button
                        onClick={handlePrev}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-cfSecondary rounded-full p-2 shadow-md hover:bg-cfCardHover transition-colors"
                      >
                        <img
                          src={ArrowLeftIcon}
                          alt="Previous"
                          className="h-6 w-6"
                        />
                      </button>

                      <button
                        onClick={handleNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-cfSecondary rounded-full p-2 shadow-md hover:bg-cfCardHover transition-colors"
                      >
                        <img
                          src={ArrowRightIcon}
                          alt="Next"
                          className="h-6 w-6"
                        />
                      </button>
                    </>
                  )}
                </div>

                {documents.length > 1 && (
                  <div className="text-center text-sm text-cfGlobalText mt-2">
                    {currentIndex + 1}/{documents.length}
                  </div>
                )}

                {documents.length > 1 && (
                  <div className="flex justify-center mt-2">
                    {documents.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentIndex(index);
                          setScale(1);
                          setIsLoading(true);
                        }}
                        className={`h-2 w-2 mx-1 rounded-full transition-colors ${
                          index === currentIndex
                            ? "bg-cfTemplateTransitionColor1"
                            : "bg-cfIssuedScreenTabButtonSelected hover:bg-cfSideBarDisabledText"
                        }`}
                        aria-label={`View ${documents[index].name}`}
                      />
                    ))}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
